import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { BiUserPin } from "react-icons/bi";
import { FiChevronsLeft, FiMenu } from "react-icons/fi";

import MenuDropDown from "./MenuDropDown";
import MenuItem from "./MenuItem";
import Image from "components/Image";
import CompanyCredit from "components/CompanyCredit";
import { GetGroupIdForPath, MENU_BOTTOM_GROUPS, MENU_CONFIG_ITEM, MENU_GROUPS, MENU_TOP_ITEMS } from "./MenuConfig";

import { actionGetManual } from "redux/auth/AuthActions";
import { actionAuthClean } from "redux/auth/AuthSlice";
import { selectLoguedUser, selectManualFile } from "redux/auth/AuthSelector";

import Logo from "assets/images/png/logo.png";
import { printDocument } from "utils/PrintUtils";

const ICON_SIZE = 18;

/**
 * Menú lateral. Las opciones salen de MenuConfig; el usuario, el tema y cerrar sesión están en la barra superior.
 */
const Menu = ({ open: bIsOpen, onChange }) => {
  const oDispatch = useDispatch();
  const { pathname: sPathname } = useLocation();
  const oUser = useSelector(selectLoguedUser);
  const sManual = useSelector(selectManualFile);

  // El grupo de la pantalla actual arranca abierto
  const [sOpen, setOpen] = useState(() => GetGroupIdForPath(sPathname));

  const downloadManual = () => oDispatch(actionGetManual());

  useEffect(() => {
    if (sManual) {
      printDocument(sManual, "Manual de usuario", () => {
        oDispatch(actionAuthClean());
      });
    }
  }, [sManual]);

  const renderItem = oItem => {
    const Icon = oItem.icon;
    return (
      <MenuItem key={oItem.path} label={oItem.label} path={oItem.path} onlyIcon={!bIsOpen}>
        <Icon size={ICON_SIZE} className="shrink-0" />
      </MenuItem>
    );
  };

  const renderGroup = oGroup => {
    const Icon = oGroup.icon;
    return (
      <MenuDropDown
        key={oGroup.id}
        open={sOpen}
        title={oGroup.title}
        identifier={oGroup.id}
        onClick={setOpen}
        onlyIcon={!bIsOpen}
        defaultOpen={oGroup.id === sOpen}
        icon={<Icon size={ICON_SIZE} className="shrink-0" />}
      >
        {oGroup.items.map(renderItem)}
      </MenuDropDown>
    );
  };

  return (
    <div className={"fixed h-screen transition-all duration-300 z-40 " + (bIsOpen ? "w-64" : "w-14")}>
      <div className="h-full flex flex-col bg-white border-r border-line dark:bg-gray-900 dark:border-line-dark">
        <div className={`flex items-center h-12 shrink-0 border-b border-line dark:border-line-dark ${bIsOpen ? "px-3" : "justify-center"}`}>
          {bIsOpen && (
            <span onClick={() => onChange(!bIsOpen)} className="flex items-center gap-2 min-w-0 cursor-pointer">
              <div className="h-7 w-7 shrink-0 bg-white rounded-md overflow-hidden border border-line dark:border-line-dark">
                {
                  oUser?.config?.company?.image
                    ? <Image path={`data:image/png;base64, ${oUser?.config?.company?.image}`} />
                    : <Image path={Logo} />
                }
              </div>
              <div className="flex flex-col min-w-0 leading-tight">
                <span className="text-sm font-medium whitespace-nowrap text-gray-800 dark:text-white">Bonsai WEB</span>
                <span className="text-xs truncate text-gray-400">{oUser?.config?.company?.name}</span>
              </div>
            </span>
          )}
          <button
            type="button"
            title={bIsOpen ? "Achicar menú" : "Abrir menú"}
            onClick={() => onChange(!bIsOpen)}
            className={`p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white ${bIsOpen ? "ml-auto" : ""}`}
          >
            {bIsOpen ? <FiChevronsLeft size={18} /> : <FiMenu size={18} />}
          </button>
        </div>

        <ul className="grow px-2 py-3 space-y-0.5 overflow-y-auto scrollbar-hide">
          {MENU_TOP_ITEMS.map(renderItem)}
          <li className="pt-2" aria-hidden="true" />
          {MENU_GROUPS.map(renderGroup)}
        </ul>

        <ul className="px-2 py-2 space-y-0.5 border-t border-line dark:border-line-dark">
          {MENU_BOTTOM_GROUPS.map(renderGroup)}
          {renderItem(MENU_CONFIG_ITEM)}
          <MenuItem onlyIcon={!bIsOpen} label="Manual de usuario" onClick={downloadManual}>
            <BiUserPin size={ICON_SIZE} className="shrink-0" />
          </MenuItem>
        </ul>

        <div className="px-2 py-2 border-t border-line dark:border-line-dark">
          <CompanyCredit compact={!bIsOpen} />
        </div>
      </div>
    </div>
  );
};

export default Menu;
